import {
  getGetCustomerAuthMePushSubscriptionsQueryKey,
  useDeleteCustomerAuthMePushSubscriptions,
  useGetCustomerAuthMePushSubscriptions,
  useGetPushPublicKey,
  usePostCustomerAuthMePushSubscriptions,
} from '@agos/api-client/customers'
import { apiErrorMessage } from '@agos/api-client'
import { tt } from '@agos/shared'
import { useCallback, useState } from 'react'

import {
  hasVapidKey,
  homeScreenOnly,
  publicKeyOf,
  pushState,
  readDevice,
  subscribedHere,
  subscriptionCountOf,
  vapidKeyBytes,
  type PushState,
} from './push'
import {
  notePushPermission,
  notePushSubscribed,
  notePushUnsubscribed,
  usePushBrowser,
} from './push-browser'
import {
  closePushForThisTab,
  declinePush,
  undoPushDecline,
  usePushClosedThisTab,
  usePushDeclined,
} from './push-prompt-memory'
import { signedInCustomerId, useCustomerAuth } from './use-customer-auth'

/**
 * Push, from the browser's side — §9.239, FB-397.
 *
 * **One home, because this was already spelled twice and about to be a third time.** The banner held
 * its own copy of the browser probe and its own `enable()`; the profile held a literal `false` and no
 * way to switch anything off. §9.209's lesson is that the copy with a test hides the copy without one,
 * so the probe, the subscribe and the unsubscribe live here and both screens read them.
 *
 * **Three facts decide the state and all three can change under the page**: whether the server has a
 * VAPID key (FB-C03 — it does not, on this stack), what the browser's permission is, and whether this
 * browser holds a subscription. The permission is held rather than read once, which fixes a
 * defect found while building this: the banner captured `Notification.permission` at mount, so after a
 * successful subscribe it still read `default`, `mayEnable` stayed true and the banner offered to turn
 * on what had just been turned on. Since §9.737's review it is held once for the tab (`push-browser.ts`),
 * because a copy per reader had the same defect between two readers.
 */

export interface PushControl {
  state: PushState
  publicKey: string | null
  busy: boolean
  /** What went wrong on the last attempt, in the customer's language. Empty when nothing did. */
  problem: string
  enable: () => Promise<void>
  disable: () => Promise<void>
  /**
   * Whether `state` is an answer rather than a guess - §9.737. The browser's subscription probe has answered, and
   * the server's list has too or is not being asked. Until then a customer who is already subscribed reads as
   * `granted-inactive`, and an offer drawn from that would flash at them.
   */
  settled: boolean
  /** Whether this customer answered *Nie* on this device - shared by every reader in the tab (§9.737). */
  declined: boolean
  /** *Nie, ďakujem*: never offered again on this device for this account, until push is switched on. */
  decline: () => void
  /** Whether this customer closed the offer for this visit - this tab, the last two hours (§9.737). */
  closedThisTab: boolean
  /** *Zavrieť*: not again in this tab for this visit, asked again on the next. */
  closeForNow: () => void
}

export function usePush(): PushControl {
  const { data } = useGetPushPublicKey()
  const save = usePostCustomerAuthMePushSubscriptions()
  const drop = useDeleteCustomerAuthMePushSubscriptions()

  /* §9.737, corrected after review - **the browser's half is one per tab, not one per reader.** The offer and the
   * account's push row each kept their own copy of the permission and of the browser's subscription, read once at
   * mount, so a subscribe in the offer left the row under it saying push was off until a reload. `push-browser.ts`
   * holds them once and probes once; a subscribe or an unsubscribe here updates every reader in the same render.
   * `holds` is `null` until the probe has answered either way - the guess an offer must not be drawn from. */
  const browser = usePushBrowser()
  const supported = browser.supported
  const permission = browser.permission
  const hasSubscription = browser.holds === true
  const probed = browser.holds !== null
  // §9.734 - which phone this is decides the `home-screen` state; none of it changes under the page.
  const [device] = useState(readDevice)
  const [busy, setBusy] = useState(false)
  /* What went wrong on the last attempt, **and for whom** (corrected after review). The offer stays mounted for the
   * life of the tab, so a failure kept in plain state was shown to the next customer to sign in on the same phone,
   * as their own. A sentence belongs to the account that pressed the button. */
  const [attempt, setAttempt] = useState<{ customerId: number | null; problem: string }>({
    customerId: null,
    problem: '',
  })

  const publicKey = publicKeyOf(data)
  /* §9.648, FB-976 - **the session is one of the facts that decides the state.**
   *
   * The save is `@RequireCustomerAuth` and the row is keyed to a customer, so an anonymous visitor was offered
   * the banner, granted permission, subscribed in the browser and then met a 401. Legacy hides its banner on
   * `!user` and takes the token as a required parameter; AGOS ported the state machine and neither guard.
   *
   * Read from the store rather than passed in, because both readers - the banner and the profile row - would
   * otherwise have to remember, and forgetting is the whole failure mode. */
  const signedIn = useCustomerAuth((auth) => auth.token !== null)
  /* §9.737 - **whose answer the offer remembers.** A *Nie* belongs to the person who said it, so another account
   * on the same phone is still asked; nobody signed in has answered nothing. */
  const customerId = useCustomerAuth(signedInCustomerId)
  /* §9.734, on §10A.381 - **the server's own list, because every push is sent to it.** The browser keeps its
   * subscription after the manager removes the customer's rows, so a browser-only answer said *zapnuté* while
   * nothing was sent. Asked only where the answer can matter: a signed-in customer on a browser that can push.
   * `registeredHere` is a subscribe made in this page, which counts before the refetched count arrives.
   *
   * §9.737 - named, because the same fact decides `settled` below: a list that is asked has to answer first.
   *
   * **Keyed by the customer it is about** (corrected after review). The generated key is the path alone and the app
   * keeps a read fresh for a minute, so the next customer to sign in on the same phone was answered with the
   * previous one's count - offered push they already had, or not asked at all. With the id in the key, their own
   * read starts `pending`, and the offer waits for it. */
  const listAsked = supported && signedIn
  const {
    data: listed,
    refetch: refetchListed,
    status: listStatus,
  } = useGetCustomerAuthMePushSubscriptions({
    query: { enabled: listAsked, queryKey: [...getGetCustomerAuthMePushSubscriptionsQueryKey(), customerId] },
  })
  /* §9.737 - **the state is an answer only once both reads have answered.** A browser that cannot push has
   * nothing to probe and no list to ask, so its state (`unsupported`, `home-screen`) is final at once. A list read
   * that failed has answered too: `subscribedHere` keeps the browser's own answer then, which is today's. */
  const settled = !supported || (probed && (!listAsked || listStatus !== 'pending'))
  const declined = usePushDeclined(customerId)
  const closedThisTab = usePushClosedThisTab(customerId)
  const decline = useCallback(() => declinePush(customerId), [customerId])
  const closeForNow = useCallback(() => closePushForThisTab(customerId), [customerId])
  /* §9.734 - a subscribe made in this page counts before the server's refetched count arrives - **for the customer
   * it was made for**, which the review found it was not: the offer outlives a sign-out, and a subscribe saved to one
   * account counted as the next one's, so they were never asked. */
  const registeredHere = customerId !== null && browser.registeredFor === customerId
  const subscribed = subscribedHere({
    browserHolds: hasSubscription,
    serverCount: subscriptionCountOf(listed),
    registeredHere,
  })
  const problem = attempt.customerId === customerId ? attempt.problem : ''
  const state = pushState({
    supported,
    homeScreenOnly: homeScreenOnly(device),
    publicKey,
    signedIn,
    permission,
    hasSubscription: subscribed,
  })

  /**
   * Ask, subscribe, and tell the server — in that order, and all three.
   *
   * An earlier version stopped after `Notification.requestPermission()`. The browser then said yes and
   * nothing was subscribed to anything: no `pushManager.subscribe`, no row on the server, and a banner
   * that disappeared as though it had worked. Each step can fail in its own way and each says so,
   * because *nothing happened* is the one outcome a customer cannot act on.
   */
  const enable = useCallback(async () => {
    const say = (problem: string) => setAttempt({ customerId, problem })
    say('')
    if (publicKey === null || !hasVapidKey(publicKey)) {
      say(tt('Server zatiaľ nemá nastavené notifikácie.'))
      return
    }
    setBusy(true)
    try {
      const granted = await Notification.requestPermission()
      // Every reader sees the browser's answer, so a *Block* in the offer's prompt reaches the account's row at once.
      notePushPermission(granted)
      if (granted !== 'granted') {
        say(
          granted === 'denied'
            ? tt('Notifikácie sú zablokované - povoľte ich v nastaveniach prehliadača.')
            : tt('Povolenie zostalo nezodpovedané.'),
        )
        return
      }
      const registration = await navigator.serviceWorker.ready
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          // Required by every browser that implements push: a notification the customer can see, for
          // every message. Silent push is not on offer and asking for it fails the subscribe.
          userVisibleOnly: true,
          // `.buffer`, because the DOM types accept a `BufferSource` and a `Uint8Array` over a
          // `SharedArrayBuffer` is not one. Legacy asserts the same way.
          applicationServerKey: vapidKeyBytes(publicKey).buffer as ArrayBuffer,
        }))
      const json = subscription.toJSON()
      await save.mutateAsync({ data: { endpoint: json.endpoint, keys: json.keys } as never })
      /* §9.737, corrected after review - **every reader sees the subscribe in the same render**: the offer closes
       * because its state is `granted-active`, and the account's row under it says *zapnuté* rather than offering
       * the switch again. The first draft closed the offer for the tab here instead, because the other reader kept
       * its own copy of the browser; there is one copy now, so the offer closes for the true reason. */
      notePushSubscribed(customerId)
      // §9.737 point 4 - **switching push on undoes a *Nie*.**
      undoPushDecline(customerId)
      // §9.734 - every other reader of the count (the offer, a second tab of the profile) sees it too.
      void refetchListed()
    } catch (caught) {
      /* §9.648 - **the server's own sentence, not one sentence for three failures.** This `catch` wrapped the
       * permission, the subscribe and the save, so a 401, a refused `pushManager.subscribe` and a dropped
       * connection all read *"Predplatné sa nepodarilo uložiť"* - which is why neither the owner nor this
       * session could tell which step broke from the screen alone. Legacy returns a reason per failure and names
       * the server save separately. §9.385's rule, on the last screen that still swallowed one. */
      say(apiErrorMessage(caught, tt('Predplatné sa nepodarilo uložiť. Skúste to znova.')))
    } finally {
      setBusy(false)
    }
  }, [publicKey, save, refetchListed, customerId])

  /**
   * Two facts, so two writes — legacy's order.
   *
   * The server row is what a campaign reads and the browser subscription is what a push arrives on;
   * dropping one and leaving the other is how a customer keeps receiving notifications they switched
   * off, or how the server keeps pushing at an endpoint nobody listens to. The DELETE goes first so a
   * failure leaves the browser subscribed and the row intact — a state the next attempt can repeat.
   */
  const disable = useCallback(async () => {
    setAttempt({ customerId, problem: '' })
    if (!supported) return
    setBusy(true)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription !== null) {
        await drop.mutateAsync({ data: { endpoint: subscription.endpoint } })
        await subscription.unsubscribe()
      }
      notePushUnsubscribed()
      // §9.737 point 4 - **switching push off in the account is the customer's *Nie*.** Otherwise the offer would
      // ask, on the next visit, to switch on what they had just switched off.
      declinePush(customerId)
      void refetchListed()
    } catch (caught) {
      // §9.648 - the same reasoning as `enable` above: this wraps a server DELETE and a browser unsubscribe, and
      // one sentence for both leaves an operator guessing which half failed.
      setAttempt({
        customerId,
        problem: apiErrorMessage(caught, tt('Upozornenia sa nepodarilo vypnúť. Skúste to znova.')),
      })
    } finally {
      setBusy(false)
    }
  }, [drop, supported, refetchListed, customerId])

  return {
    state,
    publicKey,
    busy,
    problem,
    enable,
    disable,
    settled,
    declined,
    decline,
    closedThisTab,
    closeForNow,
  }
}
